/**
 * Ghost prune, Phase A1 — DRY RUN ONLY. Nothing here deletes anything.
 *
 * A ghost is a row the server soft-deleted that stays in fieldDb.records:
 * get_field_records omits it (TOOLS_ACTIVE_PREDICATE), loadSiteRecords leaves
 * it out of the returned array, and nothing removes it from IndexedDB. The
 * offline arm, the fetch-failure fallback and the create duplicate check all
 * read getRecords, so they still see it (B-097, B-048; witnessed on a phone
 * 2026-10-22).
 *
 * This module holds the two things the dry run needs and the delete (Phase A2)
 * will reuse: the complete-walk proof, and a record of what the latest Load
 * WOULD have pruned, which the records header shows on the phone.
 *
 * Kept out of fetchFieldRecords.js on purpose: the page tests mock that module
 * wholesale as { loadSiteRecords }, so a new export there would arrive as
 * undefined in every one of them.
 */

/**
 * Did this Load see the site's WHOLE active set? Absence is evidence only
 * after a complete walk; past a failed or truncated page everything looks
 * absent. Proved from the pages themselves, not from fetchFieldRecords having
 * returned: its walk breaks WITHOUT throwing when a page says has_more but
 * carries no continuation_token, and it reads a missing has_more as the end.
 *
 * Complete means all of:
 *   - at least one page, and every page is an object with a records ARRAY
 *     (persistLoadResponse reads a missing array as empty, which would make
 *     every stored row look absent);
 *   - every page but the last says has_more === true with a continuation_token;
 *   - the last page says has_more === false — the boolean, not merely
 *     "not true".
 *
 * What it cannot see: a server that ends the walk early and says so honestly
 * (has_more false on a short page). get_field_records sets has_more from
 * whether the page had items and a next token, so an empty page that Cosmos
 * returns mid-query would end the walk there. That is server-side and out of
 * this phase's scope.
 *
 * @param {Object[]} pages — fetchFieldRecords' return value
 * @returns {boolean}
 */
export function isCompleteWalk(pages) {
  if (!Array.isArray(pages) || pages.length === 0) return false;
  for (let i = 0; i < pages.length; i += 1) {
    const page = pages[i];
    if (!page || typeof page !== 'object' || !Array.isArray(page.records)) return false;
    const last = i === pages.length - 1;
    if (last) {
      if (page.has_more !== false) return false;
    } else if (page.has_more !== true || !page.continuation_token) {
      return false;
    }
  }
  return true;
}

// The latest Load's result. One slot: the host reads it right after
// loadSiteRecords resolves, keyed by site so a stale slot never answers for
// another site.
let latest = null;

/** Forget the latest result — called as each Load starts. */
export function clearPruneDryRun() {
  latest = null;
}

/**
 * Record and log what the Load just finished would have pruned.
 *
 * status:
 *   'complete'                  — the set is the rows that failed the keep test
 *   'skipped-incomplete-walk'   — the walk is not proved complete; set is empty
 *   'skipped-queue-unreadable'  — the queue read failed, so every leftover was
 *                                 kept; set is empty
 *
 * @param {string} siteId
 * @param {{complete: boolean, queueRead: boolean, candidates: Object[], storedCount: number}} input
 *   candidates — the leftovers that failed the keep test
 * @returns {Object} the recorded result
 */
export function reportPruneDryRun(siteId, { complete, queueRead, candidates, storedCount }) {
  let status = 'complete';
  if (!complete) status = 'skipped-incomplete-walk';
  else if (!queueRead) status = 'skipped-queue-unreadable';
  const rows = status === 'complete' ? candidates : [];
  latest = {
    siteId,
    status,
    count: rows.length,
    storedCount,
    rows: rows.map((r) => ({
      record_id: r.record_id,
      tool_id: r.tool_id ?? null,
      supplier_tool_id: r.working?.supplier_tool_id ?? r.baseline?.supplier_tool_id ?? null,
    })),
  };
  console.info(
    `[fieldPrune] DRY RUN site=${siteId}: ${status} — would prune ${latest.count} of ${storedCount} stored rows (nothing deleted)`,
  );
  if (latest.count > 0) console.table(latest.rows);
  return latest;
}

/**
 * @param {string} siteId
 * @returns {Object|null} the latest result for this site, or null
 */
export function getPruneDryRun(siteId) {
  return latest && latest.siteId === siteId ? latest : null;
}

/* ── A1.5: the header line's opt-in ───────────────────────────────────────────
   The console log above is unconditional. The "Prune dry run" header line is
   not: it shows only on a device where someone turned it on, by tapping the
   Build stamp OPT_IN_TAPS times inside OPT_IN_WINDOW_MS. Off by default, so a
   rep never sees it and a normal deploy leaves it off.

   A device-local flag rather than a URL parameter or an identity check: an
   iPhone home-screen web app has no address bar and does not share storage with
   Safari, so the switch has to be reachable from inside the app, and nothing on
   the server changes. Storage can throw (private mode, blocked site data); a
   failed read is off and a failed write is ignored. */
export const PRUNE_DRY_RUN_OPT_IN_KEY = 'toolsight-field-prune-dryrun';
export const OPT_IN_TAPS = 5;
export const OPT_IN_WINDOW_MS = 3000;

export function readPruneDryRunOptIn() {
  try {
    return localStorage.getItem(PRUNE_DRY_RUN_OPT_IN_KEY) === '1';
  } catch (err) {
    return false;
  }
}

export function writePruneDryRunOptIn(on) {
  try {
    if (on) localStorage.setItem(PRUNE_DRY_RUN_OPT_IN_KEY, '1');
    else localStorage.removeItem(PRUNE_DRY_RUN_OPT_IN_KEY);
  } catch (err) {
    // the switch simply does not persist on this device
  }
}

/**
 * One tap on the Build stamp. Keeps the taps still inside the window; the
 * OPT_IN_TAPS-th one toggles and starts the count again.
 *
 * @param {number[]} taps — earlier tap times (ms)
 * @param {number} now
 * @returns {{taps: number[], toggle: boolean}}
 */
export function registerOptInTap(taps, now) {
  const recent = [...taps.filter((t) => now - t < OPT_IN_WINDOW_MS), now];
  return recent.length >= OPT_IN_TAPS ? { taps: [], toggle: true } : { taps: recent, toggle: false };
}
